import { Navigate, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { getActiveClientId } from '@/utils/clientWorkspace';

// Helper function to check if JWT token is expired
const isTokenExpired = (token) => {
  if (!token) return true;
  
  try {
    // JWT tokens are base64 encoded with 3 parts: header.payload.signature
    const parts = token.split('.');
    if (parts.length !== 3) return true;
    
    // Decode the payload (second part)
    const payload = JSON.parse(atob(parts[1]));
    
    // Check if token has expiration claim
    if (!payload.exp) return false; // No expiration, assume valid
    
    // Check if current time is past expiration (exp is in seconds)
    const currentTime = Math.floor(Date.now() / 1000);
    return currentTime >= payload.exp;
  } catch (error) {
    console.error('Error checking token expiration:', error);
    return true; // Assume expired if we can't parse
  }
};

// Helper function to clear auth data and redirect
const clearAuthData = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
};

export default function ProtectedRoute({ children }) {
  const location = useLocation();
  const [isChecking, setIsChecking] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  
  useEffect(() => {
    const token = localStorage.getItem('token');
    
    if (!token) {
      setIsAuthenticated(false);
      setIsChecking(false);
      return;
    }
    
    // Check if token is expired
    if (isTokenExpired(token)) {
      console.log('Token expired, clearing auth data');
      clearAuthData();
      setIsAuthenticated(false);
      setIsChecking(false);
      return;
    }
    
    setIsAuthenticated(true);
    setIsChecking(false);
  }, [location.pathname]);
  
  // Show nothing while checking (prevents flash)
  if (isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0B2B5C] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    // Redirect to login with the return URL
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  if (
    user.account_type === 'team_member' &&
    !getActiveClientId() &&
    location.pathname !== '/team-members'
  ) {
    return <Navigate to="/team-members" replace />;
  }
  
  return children;
}

// Export utility functions for use elsewhere
export { isTokenExpired, clearAuthData };
